import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSession } from "../lib/auth";

export function SignIn() {
  const { refreshSession } = useSession();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTestAuthEnabled, setIsTestAuthEnabled] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState("/");

  useEffect(() => {
    const stored = sessionStorage.getItem("redirectAfterSignIn");
    if (stored) {
      setRedirectUrl(stored);
    }
  }, []);

  useEffect(() => {
    async function fetchConfig() {
      const res = await api.test.config.$get();
      if (res.ok) {
        const data = await res.json();
        setIsTestAuthEnabled(data.enableTestAuth);
      }
      setConfigLoaded(true);
    }
    fetchConfig().catch(() => setConfigLoaded(true));
  }, []);

  useEffect(() => {
    async function fetchCsrf() {
      const res = await fetch("/api/auth/csrf");
      const data: { csrfToken: string } = await res.json();
      setCsrfToken(data.csrfToken);
    }
    fetchCsrf().catch(console.error);
  }, []);

  const handleTestSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError(null);

    try {
      const res = await api.test["sign-in"].$post({
        json: { email },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error("error" in data ? data.error : "Sign in failed");
      }

      sessionStorage.removeItem("redirectAfterSignIn");
      await refreshSession();
      window.location.href = redirectUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed");
    } finally {
      setIsLoading(false);
    }
  };

  if (!configLoaded) {
    return (
      <div className="mx-auto max-w-sm pt-12">
        <h1 className="mb-6 text-center text-2xl font-bold">Sign In</h1>
        <p className="text-center text-text-muted">Loading...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm pt-12">
      <h1 className="mb-6 text-center text-2xl font-bold">Sign In</h1>

      {isTestAuthEnabled && (
        <>
          <form onSubmit={handleTestSignIn} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="mb-1 block text-sm text-text-muted"
              >
                Email (Test Auth)
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-full rounded border border-border-subtle bg-bg-secondary px-3 py-2"
                required
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded bg-bg-secondary px-4 py-2 text-text-primary border border-border-subtle hover:bg-bg-tertiary disabled:opacity-50"
            >
              {isLoading ? "Signing in..." : "Sign In with Test Auth"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-border-subtle" />
            <span className="text-sm text-text-muted">or</span>
            <div className="h-px flex-1 bg-border-subtle" />
          </div>
        </>
      )}

      <div className="text-center">
        <p className="mb-4 text-text-muted">Sign in with your Google account</p>
        <form action="/api/auth/signin/google" method="POST">
          <input type="hidden" name="csrfToken" value={csrfToken ?? ""} />
          <input type="hidden" name="callbackUrl" value={redirectUrl} />
          <button
            type="submit"
            disabled={!csrfToken}
            className="w-full rounded bg-amber px-6 py-2 text-on-accent hover:bg-amber/90 disabled:opacity-50"
          >
            Sign In with Google
          </button>
        </form>
      </div>
    </div>
  );
}
